import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import TextField from 'UI/components/TextField';
import { AttachedFile } from 'UI/components/Forms/FeedbackForm/Upload/AttachedFile';
import Svg from 'UI/components/Svg';
import { SVG_IMAGES_TYPES } from 'utils/constants';
import {
  useSendContactFormMutation,
  CONTACT_CASH_KEY,
} from 'redux/apis/dataSending';
import classNames from 'classnames';
import { PriceSelector } from './Prices';
import { FormAlert } from './FormAlert';
import useUpload from '../model/use-upload';
import styles from './styles.module.scss';

export const ContactForm = ({
  onClose,
  title,
  description,
  open,
}) => {
  const [selectedFiles, setFiles] = useState([]);
  const [sendForm, {
    isSuccess,
    isLoading,
    isError,
  }] = useSendContactFormMutation({ fixedCacheKey: CONTACT_CASH_KEY });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { dirtyFields },
  } = useForm();

  const {
    handleFilesChange,
    handleOnUnpinFile,
    updateSelectedFileInfo,
  } = useUpload({
    setFiles,
    selectedFiles,
  });

  const submitHandler = handleSubmit(async (values, event) => {
    event.preventDefault();
    const attachments = selectedFiles.map((file) => file.signedUrl);

    await sendForm({
      ...values,
      attachments,
      projectBudget: values.price || '',
      description: values.description,
    });

    if (isSuccess) {
      setFiles([]);
      reset();
    }
  });

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [
    open,
    reset,
  ]);

  return (
    <>
      <form
        className={classNames(styles.form, { [styles.onSuccess]: isSuccess })}
        onSubmit={submitHandler}
      >
        {title && (
          <h4 className={styles['form-title']}>
            {title}
          </h4>
        )}
        {description && (
          <p className={styles['form-description']}>{description}</p>
        )}
        <TextField
          name="name"
          register={register}
          placeholder="Name *"
          errorMessage="Required field"
          required={dirtyFields.name}
          className={styles.input}
        />
        <TextField
          name="phone"
          register={register}
          placeholder="Phone number"
          errorMessage="Incorrect phone number"
          type="tel"
          className={styles.input}
        />
        <TextField
          name="email"
          register={register}
          placeholder="Email *"
          errorMessage="Incorrect email address"
          required={dirtyFields?.email}
          type="email"
          className={styles.input}
        />
        <TextField
          name="description"
          register={register}
          placeholder="Description *"
          errorMessage="Required field"
          required={dirtyFields?.description}
          className={styles.input}
          rows={3}
          textarea
        />
        <PriceSelector
          register={register}
          setValue={setValue}
          getValues={getValues}
        />
        <div className={styles.uploadFile}>
          <label htmlFor="file-input">
            <Svg type={SVG_IMAGES_TYPES.attachment} />
            <span>Attach file</span>
            <input
              id="file-input"
              type="file"
              onChange={handleFilesChange}
              multiple
              className={styles.hide}
            />
          </label>
        </div>
        <div className={styles['form-footer']}>
          <button
            type="button"
            onClick={onClose}
            className={styles['cancel-button']}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={styles['submit-button']}
            disabled={isLoading}
          >
            Send message
          </button>
        </div>
        {isError && (
          <p className={styles['error-message']}>
            There was an error trying to send your message. Please try again later
          </p>
        )}
        <div className={styles.attachedFiles}>
          {selectedFiles?.map((file) => (
            <AttachedFile
              key={file.signedUrl}
              handleOnUnpinFile={handleOnUnpinFile}
              currentFile={file}
              updateSelectedFileInfo={updateSelectedFileInfo}
            />
          ))}
          {selectedFiles && !!selectedFiles?.length && (
            <div className={styles.moreFilesLink}>
              <label htmlFor="file-input-more">
                add more files
                <input
                  id="file-input-more"
                  type="file"
                  onChange={handleFilesChange}
                  multiple
                  className={styles.hide}
                />
              </label>
            </div>
          )}
        </div>

      </form>
      <FormAlert />
    </>
  );
};
